import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Clock,
  XCircle,
  ArrowLeft,
  ArrowRight,
  MessageCircle,
  MapPin,
  CreditCard,
} from "lucide-react";

import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { useCart } from "../context/CartContext";
import { getOrder } from "../lib/api";
import { formatPrice } from "../lib/filters";
import { WHATSAPP_URL } from "../lib/site";

const SUCCESS = "SUCCESS";
const FAILED = "FAILED";

const formatDate = (ts) => {
  if (!ts) return "";
  const d = new Date(String(ts).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return String(ts);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const chip = "rounded-full border border-beige px-3 py-1 text-xs text-charcoal/70";

export default function OrderDetail() {
  const { orderId } = useParams();
  const { clear } = useCart();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (order?.status === SUCCESS) clear();
  }, [order, clear]);

  useEffect(() => {
    if (!orderId) return;
    let tries = 0;
    const fetchOrder = async () => {
      try {
        const o = await getOrder(orderId);
        setOrder(o);
        if (![SUCCESS, FAILED].includes(o.status) && tries < 3) {
          tries += 1;
          setTimeout(fetchOrder, 1500);
        }
      } catch {
        setError(true);
      }
    };
    fetchOrder();
  }, [orderId]);

  if (error) {
    return (
      <div data-testid="order-detail" className="bg-ivory min-h-screen">
        <Header />
        <main className="pt-28 md:pt-32 pb-28 lg:pb-24">
          <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
            <h1 className="font-serif font-light text-charcoal text-4xl md:text-5xl tracking-tight">
              Order not found
            </h1>
            <p className="mt-4 text-charcoal/65">
              We couldn't find an order with id <span className="font-medium text-charcoal">{orderId}</span>.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/orders"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-charcoal text-ivory px-8 py-4 text-sm font-medium btn-fill hover:text-charcoal transition-colors duration-500"
              >
                <ArrowLeft size={16} strokeWidth={1.5} /> Track orders
              </Link>
              <a
                href={WHATSAPP_URL(`Hi BathLane, I can't find my order ${orderId}. Can you help?`)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-charcoal px-8 py-4 text-sm font-medium text-charcoal hover:bg-stone/60 transition-colors duration-300"
              >
                <MessageCircle size={16} strokeWidth={1.5} /> Ask on WhatsApp
              </a>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const status = order?.status ?? "INITIATED";
  const headline =
    status === SUCCESS
      ? "Your order is confirmed"
      : status === FAILED
        ? "Your order wasn't completed"
        : "We're working on your order";

  const body =
    status === SUCCESS
      ? "Thank you for your order. We'll confirm dispatch timelines and Jaipur installation support shortly."
      : status === FAILED
        ? "It looks like the payment wasn't completed. No charges were made — you can try again or reach us on WhatsApp."
        : "We're confirming your order with our team. You'll receive an update on WhatsApp shortly.";

  const Icon = status === SUCCESS ? CheckCircle2 : status === FAILED ? XCircle : Clock;

  const customer = order?.customer ?? null;
  const paymentLabel = order?.payment_type === "PREPAID" ? "Prepaid" : "Cash on delivery";
  const paymentStatus =
    order?.payment_status === "PAID"
      ? "Payment received"
      : order?.payment_status === "FAILED"
        ? "Payment failed"
        : paymentLabel;

  return (
    <div data-testid="order-detail" className="bg-ivory min-h-screen">
      <Header />

      <main className="pt-28 md:pt-32 pb-28 lg:pb-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="mb-10">
            <Link
              to="/orders"
              className="inline-flex items-center gap-2 text-sm font-medium text-charcoal link-sweep"
            >
              <ArrowLeft size={15} strokeWidth={1.5} /> All orders
            </Link>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="text-center">
              <span className="grid place-items-center h-20 w-20 rounded-full bg-stone text-charcoal mx-auto">
                <Icon size={34} strokeWidth={1.3} />
              </span>
              <h1 className="font-serif font-light text-charcoal text-4xl md:text-5xl mt-8 tracking-tight">
                {headline}
              </h1>
              {order && (
                <p data-testid="order-id" className="mt-3 eyebrow text-taupe">
                  Order ID · {order.order_id}
                  <span className="mx-2">·</span>
                  {formatDate(order.created_at)}
                </p>
              )}
              <p className="mt-5 max-w-md mx-auto text-charcoal/65 leading-relaxed">{body}</p>
            </div>

            {order?.items?.length > 0 && (
              <div className="mt-12 border border-beige rounded-sm divide-y divide-beige">
                {order.items.map((i, idx) => (
                  <div key={idx} data-testid="order-item" className="flex items-center gap-5 px-6 py-5">
                    <span className="grid place-items-center h-20 w-20 rounded-full border border-beige bg-stone overflow-hidden shrink-0">
                      {i.image ? (
                        <img src={i.image} alt={i.name ?? ""} className="h-full w-full object-cover" />
                      ) : (
                        <span className="text-[10px] text-taupe px-1 text-center leading-tight">
                          {(i.name ?? "Item").slice(0, 22)}
                        </span>
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-serif text-lg text-charcoal">{i.name}</p>
                      <div className="mt-2.5 flex flex-wrap gap-2">
                        <span className={chip}>Qty {i.qty ?? 1}</span>
                        {i.width && <span className={chip}>{i.width}″ wide</span>}
                        {i.finish && <span className={chip}>{i.finish}</span>}
                        {i.sink && <span className={chip}>{i.sink}</span>}
                      </div>
                    </div>
                    <span className="font-serif text-lg text-charcoal whitespace-nowrap">
                      {formatPrice((Number(i.price) || 0) * (i.qty || 1))}
                    </span>
                  </div>
                ))}

                <div className="px-6 py-5">
                  <div className="flex items-baseline justify-between gap-4 text-sm">
                    <span className="text-charcoal/70 flex items-center gap-2">
                      <CreditCard size={14} strokeWidth={1.4} /> {paymentStatus}
                    </span>
                    <span className="text-charcoal/70">Taxes inclusive (GST)</span>
                  </div>
                  <div className="mt-4 pt-4 border-t border-beige flex items-center justify-between">
                    <span className="font-serif text-xl text-charcoal">Total</span>
                    <span data-testid="order-total" className="font-serif text-2xl text-charcoal">
                      {formatPrice(
                        typeof order.total === "number"
                          ? order.total
                          : order.items.reduce((s, i) => s + (Number(i.price) || 0) * (i.qty || 1), 0)
                      )}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {customer && (
              <div data-testid="order-customer" className="mt-6 border border-beige rounded-sm px-6 py-5">
                <h2 className="eyebrow text-taupe mb-4">Delivering to</h2>
                <p className="text-sm text-charcoal">
                  {[customer.name, customer.first_name && customer.last_name
                    ? `${customer.first_name} ${customer.last_name}`
                    : null]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </p>
                <p className="mt-1.5 text-sm text-charcoal/70 flex items-center gap-2">
                  <MapPin size={14} strokeWidth={1.4} />
                  {[customer.address, customer.city, customer.state, customer.pincode]
                    .filter(Boolean)
                    .join(", ") || "Address shared with our team"}
                </p>
                {(customer.phone || customer.email) && (
                  <p className="mt-1.5 text-sm text-charcoal/70">
                    {customer.phone ? `+91 ${customer.phone}` : ""}
                    {customer.phone && customer.email ? " · " : ""}
                    {customer.email ? customer.email : ""}
                  </p>
                )}
              </div>
            )}

            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={WHATSAPP_URL(
                  order ? `Hi BathLane, I just checked order ${order.order_id} — can you confirm the dispatch timeline?` : "Hi BathLane, I need help with my order."
                )}
                target="_blank"
                rel="noreferrer"
                data-testid="order-whatsapp"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-charcoal px-8 py-4 text-sm font-medium text-charcoal hover:bg-stone/60 transition-colors duration-300"
              >
                <MessageCircle size={16} strokeWidth={1.5} /> Ask on WhatsApp
              </a>
              <Link
                to="/vanities"
                data-testid="continue-shopping"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-charcoal text-ivory px-8 py-4 text-sm font-medium btn-fill hover:text-charcoal transition-colors duration-500"
              >
                Continue Shopping <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
